import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NameplateService } from '../../../services/nameplates/nameplate.service';
import { CheckoutModal } from './components/checkout-modal/checkout-modal';
import { LootOpeningModal, RewardItem } from './components/loot-opening-modal/loot-opening-modal';

export interface CatalogItem {
  id: string;
  name: string;
  category: 'Subscriptions' | 'Nameplates';
  price: number;
  rarity: 'Common' | 'Rare' | 'Epic' | 'Legendary';
  imageUrl: string;
  description: string;
  priceId?: string;
  isSubscription?: boolean;
  badge?: string;
  planTier?: string;
}

@Component({
  selector: 'app-store',
  imports: [CommonModule, FormsModule, CheckoutModal, LootOpeningModal],
  templateUrl: './store.html',
  styleUrl: './store.scss',
})
export class Store {
  // Navigation / Search signals for the store catalog
  searchQuery = signal('');
  selectedCategory = signal<string>('All');
  categories = signal<string[]>(['All', 'Subscriptions', 'Nameplates']);

  // Store Catalog Products (Subscriptions + Purchasable Non-Exclusive Nameplates)
  catalogItems = signal<CatalogItem[]>([
    {
      id: 'prod_V0ngRwyzWNjbou',
      priceId: 'price_1U0q7fCIybDFDaGtdyrA4yh5',
      name: 'Supporter Tier Membership',
      category: 'Subscriptions',
      price: 20.00,
      rarity: 'Epic',
      imageUrl: 'images/name-plates/supporter-nameplate.gif',
      description: '1-Year Supporter Membership ($20/yr). Priority matchmaking queue, supporter badge, and custom username chat color.',
      isSubscription: true,
      planTier: 'Supporter',
      badge: 'POPULAR TIER'
    },
    {
      id: 'sub-vip-yearly',
      priceId: 'price_1U17L7CIybDFDaGtKfkAAANk',
      name: 'VIP Tier Membership',
      category: 'Subscriptions',
      price: 50.00,
      rarity: 'Legendary',
      imageUrl: 'images/name-plates/vip-nameplate.gif',
      description: '1-Year VIP Membership ($50/yr). All Supporter perks + exclusive VIP Gold Nameplate, VIP glow effect, and premier lobby status.',
      isSubscription: true,
      planTier: 'VIP',
      badge: 'VIP TIER'
    },
    {
      id: '3',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Sand Dunes Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Rare',
      imageUrl: 'images/name-plates/sand-nameplate.gif',
      description: 'Equip the animated Sandstorm profile header frame to stand out in lobbies.'
    },
    {
      id: '4',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Dire Wolf Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Epic',
      imageUrl: 'images/name-plates/wolf-nameplate.webp',
      description: 'Equip the ultra-premium Dire Wolf profile frame on your player card.'
    },
    {
      id: '6',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Cyber Core Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Rare',
      imageUrl: 'images/name-plates/cyber-nameplate.gif',
      description: 'Futuristic neon cyberpunk grid header for your player card.'
    },
    {
      id: '7',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Infernal Flame Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Epic',
      imageUrl: 'images/name-plates/fire-nameplate.gif',
      description: 'Volcanic magma and fiery inferno embers banner background.'
    },
    {
      id: '5',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Cosmic Void Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Epic',
      imageUrl: 'images/name-plates/cosmic-nameplate.png',
      description: 'Deep purple galactic space nebula with golden stardust.'
    },
    {
      id: '9',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Frostbite Glacial Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Rare',
      imageUrl: 'images/name-plates/frost-nameplate.gif',
      description: 'Sharp cyan glacial ice crystals and frozen blizzard light.'
    },
    {
      id: '8',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Royal Champion Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Legendary',
      imageUrl: 'images/name-plates/gold-nameplate.png',
      description: 'Luxurious golden filigree armor and radiant champion rays.'
    },
    {
      id: '30',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Purple Circuit Board Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Rare',
      imageUrl: 'images/name-plates/circuit-nameplate.png',
      description: 'Deep purple glowing PCB circuit traces with neon violet nodes and microchip details.'
    },
    {
      id: '31',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Tropical Beach Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Rare',
      imageUrl: 'images/name-plates/beach-nameplate.png',
      description: 'Serene golden sand beach with rolling turquoise ocean waves and warm sunset sparkles.'
    },
    {
      id: '32',
      priceId: 'price_1U19HQCIybDFDaGtcRIJ8OQC',
      name: 'Celebration Fireworks Nameplate',
      category: 'Nameplates',
      price: 5.00,
      rarity: 'Epic',
      imageUrl: 'images/name-plates/fireworks-nameplate.png',
      description: 'Dazzling night sky celebration featuring multi-colored exploding fireworks and glittering trails.'
    },
    {
      id: '33',
      priceId: 'price_1U19o0CIybDFDaGtUcNix5Xg',
      name: 'Golden Origami Crane Nameplate',
      category: 'Nameplates',
      price: 10.00,
      rarity: 'Epic',
      imageUrl: 'images/name-plates/origami-nameplate.png',
      description: 'Geometric folded paper crane with glowing cyan energy crease lines and floating golden geometric particles.'
    },
    {
      id: '34',
      priceId: 'price_1U19o0CIybDFDaGtUcNix5Xg',
      name: 'Papercraft Pop-Up World Nameplate',
      category: 'Nameplates',
      price: 10.00,
      rarity: 'Legendary',
      imageUrl: 'images/name-plates/paperworld-nameplate.png',
      description: 'Magical 3D pop-up papercraft landscape with unfolding paper terrain, pop-up mountains, floating paper clouds, and golden seam glows.'
    }
  ]);


  // Selected item for checkout
  checkoutItem = signal<any | null>(null);

  // Computed filter for the bottom catalog
  filteredCatalogItems = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const cat = this.selectedCategory();

    return this.catalogItems().filter(item => {
      const matchesCategory = cat === 'All' || item.category === cat;
      const matchesSearch = !query ||
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query);
      return matchesCategory && matchesSearch;
    });
  });

  // Carousel actions
  selectCategory(category: string) {
    this.selectedCategory.set(category);
  }

  public nameplateService = inject(NameplateService);

  constructor() {
    this.nameplateService.getOwned().subscribe();
  }

  isOwnedItem(item: CatalogItem): boolean {
    if (item.category !== 'Nameplates') return false;
    const owned = this.nameplateService.ownedNameplates();
    if (!owned || !Array.isArray(owned)) return false;
    return owned.some((o: any) => {
      const ownedId = (o.nameplateId ?? o.NameplateId ?? o.id ?? o.Id)?.toString();
      return ownedId === item.id.toString();
    });
  }

  getRarityClass(rarity: CatalogItem['rarity']): string {
    switch (rarity) {
      case 'Legendary': return 'rarity-legendary';
      case 'Epic': return 'rarity-epic';
      case 'Rare': return 'rarity-rare';
      default: return 'rarity-common';
    }
  }

  openCheckout(item: any) {
    this.checkoutItem.set(item);
  }

  closeCheckout() {
    this.checkoutItem.set(null);
  }

  // Chest reward opening signals
  unlockedReward = signal<RewardItem | null>(null);

  handlePurchaseSuccess(item: any) {
    if (item.category === 'Nameplates' && !item.isSubscription) {
      this.nameplateService.getOwned().subscribe();
    }
  }

  closeLootOpening() {
    this.unlockedReward.set(null);
  }
}

